import React, { ReactNode, useEffect, useRef, useState } from "react";
import Portal from "elements/Portal";
import { WEBSITE_BUILDER_PORTAL } from "../../WebsiteBuilderEditor";
import { WEBSITE_BUILDER_Z_LAYERS } from "../../utility/websiteBuilderZLayers";
import "./websiteBuilderRichTextSelect.scss";

interface IWebsiteBuilderRichTextSelectProps<T extends string | number> {
  id: string;
  label: string;
  trigger: ReactNode;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  onPreview?: (value: T) => void;
}

export function WebsiteBuilderRichTextSelect<T extends string | number>(props: IWebsiteBuilderRichTextSelectProps<T>) {
  const { id, label, trigger, value, options, onChange, onPreview } = props;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      onPreview?.(null);
    }
  }, [open, onPreview]);

  useEffect(() => {
    const button = triggerRef.current;
    const popover = popoverRef.current;

    popover.popover = "auto";
    button.popoverTargetElement = popover;

    function handleBeforeToggle() {
      popover.style.visibility = "hidden";
    }

    function handleToggle() {
      const visible = popover.matches(":popover-open");
      setOpen(visible);

      if (visible) {
        const { width, height } = popover.getBoundingClientRect();
        
        const anchor = button.getBoundingClientRect();
        const top = Math.max(8, Math.min(anchor.bottom + 8, window.innerHeight - height - 8));

        popover.style.left = `${Math.max(8, Math.min(anchor.left, window.innerWidth - width - 8))}px`;
        popover.style.top = `${top}px`;
        popover.style.visibility = "visible";

        const selectedOption = popover.querySelector<HTMLElement>('[aria-selected="true"]');
        const optionToFocus = selectedOption ?? popover.querySelector<HTMLElement>('[role="option"]');

        if (optionToFocus != null) {
          optionToFocus.focus();
        }
      }
    }

    function dismiss() {
      if (popover.matches(":popover-open")) {
        popover.hidePopover();
      }
    }

    function handleScroll(event: Event) {
      if (event.target instanceof Node && !popover.contains(event.target)) {
        dismiss();
      }
    }

    popover.addEventListener("beforetoggle", handleBeforeToggle);
    popover.addEventListener("toggle", handleToggle);
    window.addEventListener("resize", dismiss);
    document.addEventListener("scroll", handleScroll, true);

    return () => {
      popover.removeEventListener("beforetoggle", handleBeforeToggle);
      popover.removeEventListener("toggle", handleToggle);
      window.removeEventListener("resize", dismiss);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  function selectOption(nextValue: T) {
    popoverRef.current.hidePopover();
    onChange(nextValue);
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="rich-text-menu-button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={id}
        onPointerDown={event => event.stopPropagation()}
      >
        {trigger}
      </button>
      <Portal parentId={WEBSITE_BUILDER_PORTAL}>
        <div
          ref={popoverRef}
          id={id}
          role="listbox"
          aria-label={label}
          className="website-builder-rich-text-select-popover"
          style={{ zIndex: WEBSITE_BUILDER_Z_LAYERS.portal }}
          onPointerDown={event => event.stopPropagation()}
          onBlur={event => {
            if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) {
              popoverRef.current.hidePopover();
            }
          }}
          onKeyDown={event => {
            if (event.key === "Escape") {
              event.stopPropagation();
            }
          }}
        >
          {options.map((option, index) => (
            <div
              key={option.value}
              className="website-builder-rich-text-select-option"
              role="option"
              aria-label={option.label}
              aria-selected={option.value === value}
              tabIndex={option.value === value ? 0 : -1}
              onMouseEnter={() => onPreview?.(option.value)}
              onFocus={() => onPreview?.(option.value)}
              onClick={() => selectOption(option.value)}
              onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  selectOption(option.value);
                } else if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
                  event.preventDefault();
                  const items = popoverRef.current.querySelectorAll<HTMLElement>('[role="option"]');
                  const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1
                    : (index + (event.key === "ArrowDown" ? 1 : items.length - 1)) % items.length;
                  items[next]?.focus();
                }
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      </Portal>
    </>
  );
}
